import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { Loader2, Lock, Send, SquarePen, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { SelectField } from '@/features/clients/SelectField';
import { Alert } from '@/components/ui/Alert';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth';
import { useUserDirectory } from '@/api/users';
import { useActiveParentRelationships } from '@/api/parentRelationships';
import {
    useFinalizeSession,
    useRecallNoteFromPortal,
    useSaveNote,
    useSendNoteToPortal,
    type NoteData,
    type NoteField,
    type NoteFieldValues,
    type NoteSignatureDef,
    type NoteSignatureValue,
    type NoteSignatureValues,
    type NoteType,
    type SessionDetail,
} from '@/api/notes';
import { SignatureCanvas, type SignatureCanvasHandle } from './SignatureCanvas';

/*
 * NoteForm — renders a session's note against its note_type definition.
 *
 * State machine (see server/src/services/sessionNotes.js for the server's
 * authoritative version):
 *
 *   ''          Not Started — editable; Save Draft only
 *   'Drafting'  In progress — editable; Save Draft always, Mark Complete
 *               enabled once all required fields/sigs present
 *   'Complete'  Locked to write_notes; finalize_notes can Finalize OR
 *               revert-to-Drafting by editing
 *   'Submitted' Sent to parent portal — frozen until Recalled
 *   'Finalized' Immutable; snapshot frozen into note_data
 *
 * Save Draft and Mark Complete are separate buttons per the 2026-04-19
 * design decision: Save Draft never auto-advances to Complete, even if
 * every field is filled. Operators cross the Complete boundary with an
 * explicit click.
 *
 * Send-to-Portal shows only when the parent signature is the LAST
 * missing thing — fields all filled, staff sigs (if present) all
 * signed, at least one parent sig unsigned. It is NOT a post-Complete
 * action; `everythingButParentSig` is the trigger.
 *
 * Layout: the form renders as a flex column so a parent with fixed
 * height can scroll the body independently of the sticky action bar.
 * The body is `flex-1 min-h-0 overflow-auto`; the footer is shrink-0
 * so Save / Mark Complete / etc. stay on-screen while the user scrolls
 * through a long note.
 */

type ActionStatus = '' | 'Drafting' | 'Complete' | 'Submitted' | 'Finalized';

interface NoteFormProps {
    session: SessionDetail;
    noteType: NoteType;
    /**
     * Called whenever the local note has unsaved edits diverging from the
     * server-side snapshot. SessionPage aggregates this with the data-
     * collection dirty state to drive the "you have unsaved work"
     * navigation guard.
     */
    onDirtyChange?: (dirty: boolean) => void;
    /**
     * Caller-provided ref that the form populates with a "save current
     * draft" function. Throws on save failure so the caller can cancel
     * navigation.
     */
    saveAllRef?: React.RefObject<(() => Promise<void>) | null>;
}

function emptyFieldValue(field: NoteField): NoteFieldValues[string] {
    if (field.type === 'checkbox') return false;
    if (field.type === 'multiselect') return [];
    return '';
}

function fieldIsDisplayOnly(field: NoteField): boolean {
    return field.type === 'heading';
}

/** Absent `required` treated as true — legacy note types stay fully gated. */
function fieldIsRequired(field: NoteField): boolean {
    if (fieldIsDisplayOnly(field)) return false;
    return field.required !== false;
}

function emptySig(): NoteSignatureValue {
    return {
        drawnPng: '',
        signedAt: null,
        signedByUserId: null,
        signedByName: '',
        role: '',
    };
}

function fieldHasValue(field: NoteField, v: NoteFieldValues[string] | undefined): boolean {
    if (fieldIsDisplayOnly(field)) return true;
    if (field.type === 'checkbox') return true;
    if (field.type === 'multiselect') return Array.isArray(v) && v.length > 0;
    return typeof v === 'string' && v.trim().length > 0;
}

/**
 * Mirrors server/src/services/sessionNotes.js signatureIsSigned. A
 * signature only counts as "signed" when the drawn bitmap AND the
 * signer name AND the role/relationship are all present — a drawn
 * bitmap with blank identity metadata isn't a valid clinical
 * attestation, so the Mark Complete / Send-to-Portal buttons must not
 * appear based on bitmap alone.
 */
function sigIsSigned(sig: NoteSignatureValue | undefined): boolean {
    if (!sig) return false;
    if (typeof sig.drawnPng !== 'string' || sig.drawnPng.length === 0) return false;
    if (!sig.signedByName?.trim()) return false;
    if (!sig.role?.trim()) return false;
    return true;
}

interface Evaluation {
    allFieldsFilled: boolean;
    allSigsSigned: boolean;
    allStaffSigsSigned: boolean;
    hasParentSig: boolean;
    everythingButParentSig: boolean;
}

function evaluate(noteType: NoteType, fields: NoteFieldValues, sigs: NoteSignatureValues): Evaluation {
    // Completion gate tracks REQUIRED fields only. Optional fields + headings
    // never block Mark Complete; mirrors server/src/services/sessionNotes.js.
    let allFieldsFilled = true;
    for (const f of noteType.fields) {
        if (fieldIsRequired(f) && !fieldHasValue(f, fields[f.id])) allFieldsFilled = false;
    }
    let allStaffSigsSigned = true;
    let allParentSigsSigned = true;
    let hasParentSig = false;
    for (const s of noteType.signatures) {
        const signed = sigIsSigned(sigs[s.id]);
        if (s.type === 'client') {
            hasParentSig = true;
            if (!signed) allParentSigsSigned = false;
        } else if (!signed) {
            allStaffSigsSigned = false;
        }
    }
    const allSigsSigned = allStaffSigsSigned && allParentSigsSigned;
    const everythingButParentSig =
        allFieldsFilled && allStaffSigsSigned && hasParentSig && !allParentSigsSigned;
    return { allFieldsFilled, allSigsSigned, allStaffSigsSigned, hasParentSig, everythingButParentSig };
}

function extractError(err: unknown, fallback: string): string {
    if (err instanceof AxiosError) {
        const msg = err.response?.data?.error;
        if (typeof msg === 'string' && msg.trim()) return msg;
    }
    if (err instanceof Error && err.message) return err.message;
    return fallback;
}

export function NoteForm({ session, noteType, onDirtyChange, saveAllRef }: NoteFormProps) {
    const user = useAuthStore((s) => s.user);
    const hasPermission = useAuthStore((s) => s.hasPermission);
    const canWrite = hasPermission('write_notes');
    const canFinalize = hasPermission('finalize_notes');

    // Active-staff directory feeds the Staff-signature picker so a user
    // signing on another user's behalf (a BCBA attesting for an RBT) can
    // pick the correct signer. We still prefill to the current user on
    // first stroke for the common self-sign path.
    const staffDirectory = useUserDirectory();

    const status = (session.noteStatus || '') as ActionStatus;
    const isFinalized = status === 'Finalized';
    const isSubmitted = status === 'Submitted';
    const isComplete = status === 'Complete';

    // Complete-state edit gate: finalize_notes can revert to Drafting.
    const editsLocked = isFinalized || isSubmitted || (isComplete && !canFinalize) || !canWrite;

    // ─── field + sig state, seeded from server's note_data ─────────────
    const seededFields = useMemo<NoteFieldValues>(() => {
        const seed: NoteFieldValues = {};
        for (const f of noteType.fields) {
            const v = session.noteData?.fields?.[f.id];
            seed[f.id] = v === undefined ? emptyFieldValue(f) : v;
        }
        return seed;
    }, [noteType.fields, session.noteData]);

    const seededSigs = useMemo<NoteSignatureValues>(() => {
        const seed: NoteSignatureValues = {};
        for (const s of noteType.signatures) {
            seed[s.id] = session.noteData?.signatures?.[s.id] ?? emptySig();
        }
        return seed;
    }, [noteType.signatures, session.noteData]);

    const [fieldVals, setFieldVals] = useState<NoteFieldValues>(seededFields);
    const [sigVals, setSigVals] = useState<NoteSignatureValues>(seededSigs);
    const [feedback, setFeedback] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
    const [confirmFinalize, setConfirmFinalize] = useState(false);

    // Dirty state is derived from a value comparison, not tracked as a
    // separate boolean. A boolean drifts: easy to forget to set it on a new
    // mutator, easy to leave true when the user undoes their edits manually,
    // and easy to reset to false on a refetch that didn't actually clear the
    // edits. Comparing the current form values to the last server snapshot
    // is always accurate.
    const isDirty = useMemo(
        () =>
            JSON.stringify(fieldVals) !== JSON.stringify(seededFields)
            || JSON.stringify(sigVals) !== JSON.stringify(seededSigs),
        [fieldVals, sigVals, seededFields, seededSigs],
    );

    // Reconcile local form state with a fresh server snapshot — but only when
    // the user is currently clean. A background refetch (window focus, query
    // invalidation) can recompute `seededFields` while the user has unsaved
    // typing in flight; replacing `fieldVals` in that case would silently
    // revert their edits. `lastSeedRef` tracks the seed we last reconciled
    // against so we can tell "clean" from "user typed something" without
    // re-checking the derived isDirty (which depends on the new seed).
    const lastSeedRef = useRef({ fields: seededFields, sigs: seededSigs });
    useEffect(() => {
        const wasClean =
            JSON.stringify(fieldVals) === JSON.stringify(lastSeedRef.current.fields)
            && JSON.stringify(sigVals) === JSON.stringify(lastSeedRef.current.sigs);
        if (wasClean) {
            setFieldVals(seededFields);
            setSigVals(seededSigs);
        }
        lastSeedRef.current = { fields: seededFields, sigs: seededSigs };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seededFields, seededSigs]);

    // ─── signature canvas refs, keyed by sig id ────────────────────────
    const canvasRefs = useRef(new Map<string, SignatureCanvasHandle>());
    const setCanvasRef = useCallback((id: string, handle: SignatureCanvasHandle | null) => {
        if (handle) canvasRefs.current.set(id, handle);
        else canvasRefs.current.delete(id);
    }, []);

    // ─── mutations ────────────────────────────────────────────────────
    const saveMut = useSaveNote();
    const finalizeMut = useFinalizeSession();
    const sendPortalMut = useSendNoteToPortal();
    const recallMut = useRecallNoteFromPortal();
    const busy = saveMut.isPending || finalizeMut.isPending || sendPortalMut.isPending || recallMut.isPending;

    // ─── evaluation (drives button visibility) ────────────────────────
    const evalState = useMemo(() => evaluate(noteType, fieldVals, sigVals), [noteType, fieldVals, sigVals]);

    // ─── field change handlers ────────────────────────────────────────
    function setField(id: string, value: NoteFieldValues[string]) {
        setFieldVals((prev) => ({ ...prev, [id]: value }));
    }

    function setSigValue(id: string, patch: Partial<NoteSignatureValue>) {
        setSigVals((prev) => ({
            ...prev,
            [id]: { ...(prev[id] ?? emptySig()), ...patch },
        }));
    }

    function handleSigStrokeEnd(sig: NoteSignatureDef) {
        const handle = canvasRefs.current.get(sig.id);
        if (!handle) return;
        const png = handle.toDataUrl();
        const prior = sigVals[sig.id] ?? emptySig();
        // Staff-sig convenience: first stroke on a blank row with no
        // explicit staff pick prefills to the current user. The staff
        // dropdown lets them override afterward if needed. Parent sigs
        // never auto-prefill — name is parent-chosen.
        const shouldPrefillFromCurrentUser =
            sig.type === 'staff' && !prior.signedByUserId && !prior.signedByName && !!user;
        setSigValue(sig.id, {
            drawnPng: png,
            signedAt: png ? new Date().toISOString() : null,
            signedByUserId: shouldPrefillFromCurrentUser
                ? (user?.id ?? null)
                : prior.signedByUserId,
            signedByName: shouldPrefillFromCurrentUser
                ? `${user!.first} ${user!.last}`.trim()
                : prior.signedByName,
            role: shouldPrefillFromCurrentUser && !prior.role
                ? (user?.roleName ?? '')
                : prior.role,
        });
    }

    function handleSigClear(sig: NoteSignatureDef) {
        const handle = canvasRefs.current.get(sig.id);
        handle?.clear();
        setSigValue(sig.id, {
            drawnPng: '',
            signedAt: null,
            signedByUserId: null,
            signedByName: '',
            role: sigVals[sig.id]?.role ?? '',
        });
    }

    // Staff-sig dropdown pick. Prefills role from the chosen user's
    // role name, but leaves the role input editable — practices
    // sometimes want to attest under a non-default role (a BCBA
    // signing as "Supervisor" rather than their formal role name).
    function handleStaffSigUserPick(sig: NoteSignatureDef, userId: string) {
        const picked = staffDirectory.data?.find((u) => u.id === userId);
        if (!picked) {
            setSigValue(sig.id, { signedByUserId: null, signedByName: '' });
            return;
        }
        setSigValue(sig.id, {
            signedByUserId: picked.id,
            signedByName: `${picked.first} ${picked.last}`.trim(),
            // Don't overwrite an already-typed role — treat dropdown
            // pick as a prefill, not a clobber.
            role: sigVals[sig.id]?.role || picked.roleName || '',
        });
    }

    // ─── save actions ─────────────────────────────────────────────────
    function buildNoteData(): NoteData {
        return { fields: fieldVals, signatures: sigVals };
    }

    // Unsaved-work guard reporting. A locked note (Finalized / Submitted /
    // read-only role) is never reported as dirty — the UI has already
    // blocked edits, so there's nothing at risk of being lost.
    const reportedDirty = isDirty && !editsLocked;
    useEffect(() => {
        onDirtyChange?.(reportedDirty);
    }, [reportedDirty, onDirtyChange]);

    // Populate the caller's saveAllRef with a "save draft now" function.
    // Throws on failure so the navigation guard can cancel the transition.
    useEffect(() => {
        if (!saveAllRef) return;
        saveAllRef.current = async () => {
            if (!reportedDirty) return;
            await saveMut.mutateAsync({ id: session.id, payload: { noteData: buildNoteData(), action: 'save' } });
        };
        return () => {
            if (saveAllRef.current) saveAllRef.current = null;
        };
        // buildNoteData closes over fieldVals/sigVals; re-register whenever
        // the draft content changes so the saved closure is current.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveAllRef, reportedDirty, fieldVals, sigVals, saveMut]);

    function onSaveDraft() {
        setFeedback(null);
        saveMut.mutate(
            { id: session.id, payload: { noteData: buildNoteData(), action: 'save' } },
            {
                onSuccess: () => setFeedback({ kind: 'success', text: 'Draft saved.' }),
                onError: () => setFeedback({ kind: 'error', text: "Couldn't save the note." }),
            },
        );
    }

    function onMarkComplete() {
        setFeedback(null);
        saveMut.mutate(
            { id: session.id, payload: { noteData: buildNoteData(), action: 'complete' } },
            {
                onSuccess: () => setFeedback({ kind: 'success', text: 'Note marked complete.' }),
                onError: () => setFeedback({ kind: 'error', text: "Couldn't mark complete." }),
            },
        );
    }

    function onFinalize() {
        setFeedback(null);
        finalizeMut.mutate(session.id, {
            onSuccess: (res) => {
                setConfirmFinalize(false);
                const claimMsg = res.claimNo ? ` Claim ${res.claimNo} created.` : '';
                setFeedback({ kind: 'success', text: `Note finalized.${claimMsg}` });
            },
            onError: (err) => {
                setConfirmFinalize(false);
                setFeedback({ kind: 'error', text: extractError(err, "Couldn't finalize the note.") });
            },
        });
    }

    async function onSendPortal() {
        setFeedback(null);
        try {
            if (reportedDirty) {
                await saveMut.mutateAsync({ id: session.id, payload: { noteData: buildNoteData(), action: 'save' } });
            }
            await sendPortalMut.mutateAsync(session.id);
            setFeedback({ kind: 'success', text: 'Sent to parent portal.' });
        } catch (err) {
            setFeedback({ kind: 'error', text: extractError(err, "Couldn't send to portal.") });
        }
    }

    function onRecall() {
        setFeedback(null);
        recallMut.mutate(session.id, {
            onSuccess: () => setFeedback({ kind: 'success', text: 'Recalled from portal.' }),
            onError: (err) => setFeedback({ kind: 'error', text: extractError(err, "Couldn't recall from portal.") }),
        });
    }

    // ─── button visibility ────────────────────────────────────────────
    const showSaveDraft    = !editsLocked;
    const showMarkComplete = !editsLocked && evalState.allFieldsFilled && evalState.allSigsSigned && !isComplete;
    const showFinalize     = isComplete && canFinalize && !isFinalized;
    const showSendPortal   = !editsLocked && evalState.everythingButParentSig;
    const showRecall       = isSubmitted && canWrite;
    const anyFooterAction  = showSaveDraft || showMarkComplete || showFinalize || showSendPortal || showRecall;

    // Save-result toast and persistent state alerts live in the non-scrolling
    // header band so they stay visible regardless of how far the user has
    // scrolled the form body. The empty-note-type alert stays in the body
    // since it's static configuration guidance, not feedback on user action.
    const hasHeaderAlert =
        !!feedback
        || isSubmitted
        || (isComplete && !canFinalize && !isFinalized);

    return (
        <div className="h-full flex flex-col min-h-0">
            <div className="shrink-0 px-5 pt-5 flex items-center justify-between">
                <h2 className="font-display text-lg font-bold text-text">{noteType.name}</h2>
                {isFinalized && (
                    <span className="inline-flex items-center gap-1 text-xs text-text-muted">
                        <Lock className="w-3 h-3" /> Frozen snapshot
                    </span>
                )}
            </div>

            {hasHeaderAlert && (
                <div role="status" aria-live="polite" className="shrink-0 px-5 pt-3 space-y-2">
                    {feedback && (
                        <Alert variant={feedback.kind === 'error' ? 'danger' : 'success'}>{feedback.text}</Alert>
                    )}
                    {isSubmitted && (
                        <Alert variant="warning">
                            Note is at the parent portal awaiting a signature. Recall to edit.
                        </Alert>
                    )}
                    {isComplete && !canFinalize && !isFinalized && (
                        <Alert variant="info">
                            This note is marked Complete. Finalizing the note creates the claim — ask a BCBA or admin to finalize.
                        </Alert>
                    )}
                </div>
            )}

            <div className="flex-1 min-h-0 overflow-auto p-5 space-y-4">
                {noteType.fields.length === 0 && noteType.signatures.length === 0 && (
                    <Alert variant="info">This note type has no fields or signatures configured.</Alert>
                )}

                {noteType.fields.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                        {noteType.fields.map((field) => (
                            <FieldRenderer
                                key={field.id}
                                field={field}
                                value={fieldVals[field.id]}
                                disabled={editsLocked}
                                onChange={(v) => setField(field.id, v)}
                            />
                        ))}
                    </div>
                )}

                {noteType.signatures.length > 0 && (
                    <div className="space-y-3">
                        <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider">Signatures</h3>
                        {noteType.signatures.map((sig) => (
                            <SignatureRow
                                key={sig.id}
                                sig={sig}
                                value={sigVals[sig.id] ?? emptySig()}
                                disabled={editsLocked}
                                staffOptions={staffDirectory.data ?? []}
                                setCanvasRef={setCanvasRef}
                                onStrokeEnd={() => handleSigStrokeEnd(sig)}
                                onClear={() => handleSigClear(sig)}
                                onFieldChange={(patch) => setSigValue(sig.id, patch)}
                                onStaffPick={(userId) => handleStaffSigUserPick(sig, userId)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {anyFooterAction && (
                <div className="shrink-0 flex flex-wrap items-center gap-2 px-5 py-3 border-t border-border bg-surface">
                    {confirmFinalize ? (
                        <>
                            <span className="mr-auto text-xs text-text-muted">
                                Finalize this note? It becomes read-only and a claim is created.
                            </span>
                            <Button
                                type="button"
                                variant="ghost"
                                size="md"
                                onClick={() => setConfirmFinalize(false)}
                                disabled={finalizeMut.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                size="md"
                                onClick={onFinalize}
                                disabled={finalizeMut.isPending}
                            >
                                <Lock className="w-3.5 h-3.5" />
                                {finalizeMut.isPending ? 'Finalizing…' : 'Finalize & Create Claim'}
                            </Button>
                        </>
                    ) : (
                        <>
                            {showSaveDraft && (
                                <Button variant="ghost" size="md" onClick={onSaveDraft} disabled={busy}>
                                    {saveMut.isPending && saveMut.variables?.payload.action === 'save' ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                        <SquarePen className="w-3.5 h-3.5" />
                                    )}
                                    Save Draft
                                </Button>
                            )}
                            {showMarkComplete && (
                                <Button variant="success" size="md" onClick={onMarkComplete} disabled={busy}>
                                    Mark Complete
                                </Button>
                            )}
                            {showSendPortal && (
                                <Button variant="primary" size="md" onClick={onSendPortal} disabled={busy}>
                                    <Send className="w-3.5 h-3.5" /> Send to Parent Portal
                                </Button>
                            )}
                            {showRecall && (
                                <Button variant="ghost" size="md" onClick={onRecall} disabled={busy}>
                                    <Undo2 className="w-3.5 h-3.5" /> Recall from Portal
                                </Button>
                            )}
                            {showFinalize && (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() => setConfirmFinalize(true)}
                                    disabled={busy}
                                >
                                    <Lock className="w-3.5 h-3.5" /> Finalize &amp; Create Claim
                                </Button>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── field renderer ──────────────────────────────────────────────────
interface FieldRendererProps {
    field: NoteField;
    value: NoteFieldValues[string] | undefined;
    disabled: boolean;
    onChange: (value: NoteFieldValues[string]) => void;
}

function FieldRenderer({ field, value, disabled, onChange }: FieldRendererProps) {
    const label = field.label || '(unnamed field)';
    const helper = field.helperText?.trim();
    const required = fieldIsRequired(field);
    const spanClass = field.fullWidth ? 'md:col-span-2' : undefined;
    const choices = Array.isArray(field.choices) ? field.choices : [];

    if (field.type === 'heading') {
        return (
            <div className="md:col-span-2 mt-2 mb-1 pb-1 border-b border-border">
                <h3 className="font-display text-sm font-bold text-text">{label}</h3>
                {helper && <p className="mt-0.5 text-xs text-text-subtle">{helper}</p>}
            </div>
        );
    }

    if (field.type === 'checkbox') {
        return (
            <div className={cn('mb-3.5 flex items-start gap-2', spanClass)}>
                <input
                    type="checkbox"
                    id={field.id}
                    checked={value === true}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-accent cursor-pointer disabled:cursor-not-allowed"
                />
                <label
                    htmlFor={field.id}
                    className="text-sm text-text select-none cursor-pointer"
                >
                    <span>
                        {label}
                        {required && <span className="text-error ml-0.5">*</span>}
                    </span>
                    {helper && <span className="block text-xs text-text-subtle">{helper}</span>}
                </label>
            </div>
        );
    }

    if (field.type === 'radio') {
        return (
            <FormField label={label} required={required} hint={helper} className={spanClass}>
                {() => (
                    <div className="flex flex-wrap gap-3 py-1">
                        {choices.map((choice) => (
                            <label key={choice} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                                <input
                                    type="radio"
                                    name={field.id}
                                    value={choice}
                                    checked={value === choice}
                                    disabled={disabled}
                                    onChange={() => onChange(choice)}
                                    className="accent-accent"
                                />
                                {choice}
                            </label>
                        ))}
                    </div>
                )}
            </FormField>
        );
    }

    if (field.type === 'select') {
        return (
            <FormField label={label} required={required} hint={helper} className={spanClass}>
                {(id) => (
                    <SelectField
                        id={id}
                        value={typeof value === 'string' ? value : ''}
                        disabled={disabled}
                        onChange={(e) => onChange(e.target.value)}
                    >
                        <option value="">—</option>
                        {choices.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </SelectField>
                )}
            </FormField>
        );
    }

    if (field.type === 'multiselect') {
        const selected = Array.isArray(value) ? value : [];
        return (
            <FormField label={label} required={required} hint={helper} className={spanClass}>
                {() => (
                    <div className="flex flex-col gap-1.5 py-1">
                        {choices.map((choice) => {
                            const checked = selected.includes(choice);
                            return (
                                <label key={choice} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={disabled}
                                        onChange={(e) => {
                                            const next = e.target.checked
                                                ? [...selected, choice]
                                                : selected.filter((c) => c !== choice);
                                            onChange(next);
                                        }}
                                        className="accent-accent"
                                    />
                                    {choice}
                                </label>
                            );
                        })}
                    </div>
                )}
            </FormField>
        );
    }

    if (field.type === 'textarea') {
        // Textareas are always full-width — the 2-column grid would squash them.
        return (
            <FormField label={label} required={required} hint={helper} className="md:col-span-2">
                {(id) => (
                    <Textarea
                        id={id}
                        rows={3}
                        value={typeof value === 'string' ? value : ''}
                        disabled={disabled}
                        onChange={(e) => onChange(e.target.value)}
                    />
                )}
            </FormField>
        );
    }

    const inputType = field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text';
    return (
        <FormField label={label} required={required} hint={helper} className={spanClass}>
            {(id) => (
                <Input
                    id={id}
                    type={inputType}
                    value={typeof value === 'string' ? value : ''}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                />
            )}
        </FormField>
    );
}

// ─── signature row ───────────────────────────────────────────────────
interface SignatureRowProps {
    sig: NoteSignatureDef;
    value: NoteSignatureValue;
    disabled: boolean;
    staffOptions: Array<{ id: string; first: string; last: string; roleName: string }>;
    setCanvasRef: (id: string, handle: SignatureCanvasHandle | null) => void;
    onStrokeEnd: () => void;
    onClear: () => void;
    onFieldChange: (patch: Partial<NoteSignatureValue>) => void;
    onStaffPick: (userId: string) => void;
}

function SignatureRow({
    sig, value, disabled, staffOptions, setCanvasRef, onStrokeEnd, onClear, onFieldChange, onStaffPick,
}: SignatureRowProps) {
    const signedAtLabel = value.signedAt
        ? new Date(value.signedAt).toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
        })
        : null;
    // Client-signature relationship picker always pulls from the practice-wide
    // Parent Relationships list managed on the Configuration page. A legacy
    // per-note-type override (`sig.relationships`) was dropped in the
    // 2026-04-23 builder redesign.
    const { data: practiceRelationships = [] } = useActiveParentRelationships();
    const relationships = practiceRelationships.map((r) => r.label);

    // Parent row: Name (free text), Relationship (dropdown from practice list).
    // Staff row:  Staff (active-staff dropdown), Role (free text, prefilled
    //             from the picked user's role).
    const isStaff = sig.type === 'staff';
    const helper  = sig.helperText?.trim();

    return (
        <div className="p-3 border border-border rounded-md bg-surface-muted/40">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-text">{sig.label || '(unnamed)'}</span>
                    <span className={cn(
                        'text-2xs uppercase tracking-wider font-bold px-1.5 py-0.5 rounded',
                        sig.type === 'client' ? 'bg-info-soft text-info-text' : 'bg-neutral-soft text-neutral-text',
                    )}>
                        {sig.type === 'client' ? 'Parent' : 'Staff'}
                    </span>
                </div>
                {signedAtLabel && (
                    <span className="text-xs text-text-subtle">Signed {signedAtLabel}</span>
                )}
            </div>

            {helper && <p className="mb-2 text-xs text-text-subtle">{helper}</p>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                {/* Name first, role/relationship second — the eye scans left
                    for "who", then rightward for context. */}
                <FormField label={isStaff ? 'Staff' : 'Name'}>
                    {(id) => (isStaff ? (
                        <SelectField
                            id={id}
                            value={value.signedByUserId ?? ''}
                            disabled={disabled}
                            onChange={(e) => onStaffPick(e.target.value)}
                        >
                            <option value="">Select staff…</option>
                            {staffOptions.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.first} {u.last}
                                </option>
                            ))}
                        </SelectField>
                    ) : (
                        <Input
                            id={id}
                            value={value.signedByName}
                            disabled={disabled}
                            onChange={(e) => onFieldChange({ signedByName: e.target.value })}
                        />
                    ))}
                </FormField>
                <FormField label={isStaff ? 'Role' : 'Relationship'}>
                    {(id) => (!isStaff ? (
                        <SelectField
                            id={id}
                            value={value.role}
                            disabled={disabled}
                            onChange={(e) => onFieldChange({ role: e.target.value })}
                        >
                            <option value="">—</option>
                            {relationships.map((r) => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </SelectField>
                    ) : (
                        // Staff role stays free-text so a user can override
                        // the prefilled role name when attesting under a
                        // non-standard title.
                        <Input
                            id={id}
                            value={value.role}
                            disabled={disabled}
                            onChange={(e) => onFieldChange({ role: e.target.value })}
                        />
                    ))}
                </FormField>
            </div>

            <SignatureCanvas
                ref={(handle) => setCanvasRef(sig.id, handle)}
                disabled={disabled}
                initialDataUrl={value.drawnPng || null}
                onChange={onStrokeEnd}
            />

            {!disabled && (
                <div className="mt-2 flex justify-end">
                    <Button variant="ghost" size="xs" type="button" onClick={onClear}>
                        Clear signature
                    </Button>
                </div>
            )}
        </div>
    );
}
